import { useState } from 'react';
import {
  CloudArrowUpIcon,
  ChartBarSquareIcon,
  CircleStackIcon,
} from '@heroicons/react/24/outline';
import API_BASE_URL from '../../../config';
import { NEWSLETTER } from '../../../content/enterpriseMessaging';
import SectionSurface from '../shared/SectionSurface';

const PILLAR_ICONS = [CloudArrowUpIcon, ChartBarSquareIcon, CircleStackIcon];

const PILLAR_ACCENT = {
  migration: 'bg-accent-50 text-accent-700 border-accent-100',
  brand: 'bg-brand-50 text-brand-600 border-brand-100',
  semantic: 'bg-[rgba(0,18,63,0.08)] text-brand-900 border-[rgba(0,18,63,0.16)]',
};

export default function Newsletter() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState({ loading: false, message: '', error: false });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, message: '', error: false });

    try {
      const res = await fetch(`${API_BASE_URL}/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (res.ok) {
        setStatus({ loading: false, message: data.message || 'Subscribed!', error: false });
        setEmail('');
      } else {
        setStatus({
          loading: false,
          message: data.error || data.message || 'Subscription failed.',
          error: true,
        });
      }
    } catch (err) {
      setStatus({ loading: false, message: 'Network error. Please try again.', error: true });
    }
  };

  return (
    <SectionSurface as="section" aria-labelledby="newsletter-heading" variant="accent-brand" bleed="top" flowTop className="section-into-footer relative isolate section">
      <div className="container-x">
        <div className="mx-auto max-w-6xl lg:max-w-7xl">
          <div className="mx-auto max-w-3xl text-center">
            <p className="eyebrow">{NEWSLETTER.eyebrow}</p>
            <h2 id="newsletter-heading" className="mt-3 text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
              {NEWSLETTER.title}
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-ink-600">{NEWSLETTER.subtitle}</p>
            <form
              className="mx-auto mt-6 flex w-full max-w-lg flex-col gap-3 sm:flex-row sm:gap-x-3"
              onSubmit={handleSubmit}
            >
              <label htmlFor="email-address" className="sr-only">
                Email address
              </label>
              <input
                id="email-address"
                name="email"
                type="email"
                required
                placeholder="you@company.com"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="min-w-0 w-full flex-auto rounded-lg border border-ink-200 bg-white px-3.5 py-2.5 text-base text-ink-900 placeholder:text-ink-400 shadow-xs focus:border-brand-400 focus:outline-none focus:ring-4 focus:ring-brand-700"
                disabled={status.loading}
              />
              <button type="submit" disabled={status.loading} className="btn-primary w-full shrink-0 sm:w-auto">
                {status.loading ? 'Subscribing…' : 'Subscribe'}
              </button>
            </form>
            {status.message && (
              <div className={`mt-3 text-sm ${status.error ? 'text-red-600' : 'text-theme-green-600'}`}>
                {status.message}
              </div>
            )}
          </div>
          <dl className="mt-12 grid min-w-0 grid-cols-1 items-stretch gap-6 text-left lg:mt-16 lg:grid-cols-3 lg:gap-8">
            {NEWSLETTER.pillars.map((pillar, i) => {
              const Icon = PILLAR_ICONS[i] || CloudArrowUpIcon;
              const accent = PILLAR_ACCENT[pillar.accent] || PILLAR_ACCENT.brand;
              return (
                <div
                  key={pillar.title}
                  className="card h-full min-w-0 border border-ink-200/90 bg-white/90 p-6 shadow-sm lg:p-8"
                >
                  <dt className="text-lg font-semibold text-ink-900">
                    <span
                      aria-hidden="true"
                      className={`flex h-10 w-10 items-center justify-center rounded-full border ${accent}`}
                    >
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="mt-4 block">{pillar.title}</span>
                  </dt>
                  <dd className="mt-2 text-base leading-relaxed text-ink-600">{pillar.body}</dd>
                </div>
              );
            })}
          </dl>
        </div>
      </div>
    </SectionSurface>
  );
}
